import { BoardState, SectionState } from '../types/task';

// How a change to the board itself (its sections, title and settings) turns the board into its next version.
// Each function takes the current board and returns a new one with the change made, leaving the original
// untouched, with no React or network in them, so they can be tested directly. data/board.ts applies them to the cache.
//
// Each tolerates a board that isn't cached (undefined) by returning it unchanged: there is then nothing to
// patch, and the board is read in full when it is next needed.

type Board = BoardState | undefined;

// Adding a section the board already has does nothing, so adding is safe to repeat.
export const withSection = (board: Board, section: SectionState): Board => {
    if (!board || board.sectionsById?.[section.id]) return board;

    return {
        ...board,
        sectionsById: { ...board.sectionsById, [section.id]: section },
        sectionOrder: [...(board.sectionOrder ?? []), section.id],
    };
};

export const withoutSection = (board: Board, sectionId: string): Board => {
    if (!board) return board;

    const { [sectionId]: _removed, ...remaining } = board.sectionsById ?? {};
    return {
        ...board,
        sectionsById: remaining,
        sectionOrder: (board.sectionOrder ?? []).filter((id) => id !== sectionId),
    };
};

export const withSectionTitle = (board: Board, sectionId: string, title: string): Board => {
    const section = board?.sectionsById?.[sectionId];
    if (!board || !section) return board;

    return { ...board, sectionsById: { ...board.sectionsById, [sectionId]: { ...section, title } } };
};

// The board-level fields an owner or editor can change.
export type BoardFields = Partial<Pick<BoardState, 'title' | 'kioskMessage' | 'kioskPhotoUrls' | 'archiveAfterDays'>>;

export const withBoardFields = (board: Board, fields: BoardFields): Board => (board ? { ...board, ...fields } : board);
